import { type TravelBookingAdminDetail } from '@utazzo/shared';
import { UzBadge, UzButton, UzCard, UzTooltip } from '@utazzo/ui';
import { Eye, EyeOff } from 'lucide-react';
import React, { useState } from 'react';

import { dateLabel } from '../booking-format';
import { useRevealDocuments } from '../use-travel-bookings';

type Traveller = {
  id: string;
  name: string;
  role: string;
  extra: string;
  passportMasked: string | null;
  passportExpiry: string | null;
  panMasked: string | null;
  ticket: string | null;
};

/**
 * Everyone on the booking, with document numbers masked by default.
 *
 * The eye reveals one traveller's full passport / PAN for this page view
 * only, and every reveal is a server call that writes a DOCUMENTS_VIEWED event
 * naming the operator. There is no "reveal all" on purpose.
 *
 * Tablets and up get a table; phones get one card per traveller, because a
 * five-column table sideways-scrolling inside a card is not readable on a
 * 390px screen.
 */
export const TravellersCard = ({
  booking,
}: {
  booking: TravelBookingAdminDetail;
}): React.JSX.Element => {
  const isHotel = booking.product === 'HOTEL';
  const travellers: Traveller[] = isHotel
    ? booking.guests.map((g) => ({
        id: g.id,
        name: `${g.title ? `${g.title} ` : ''}${g.firstName} ${g.lastName}`.trim(),
        role: g.isLeadGuest ? 'Lead guest' : g.guestType === 'CHILD' ? 'Child' : 'Guest',
        extra: `Room ${g.guestInRoom}${g.age !== null ? ` · age ${g.age}` : ''}`,
        passportMasked: g.passportNumberMasked,
        passportExpiry: g.passportExpiry,
        panMasked: g.panNumberMasked,
        ticket: null,
      }))
    : booking.passengers.map((p) => ({
        id: p.id,
        name: `${p.title ? `${p.title} ` : ''}${p.firstName} ${p.lastName}`.trim(),
        role: p.isLeadPax ? 'Lead traveller' : p.passengerType,
        extra: [
          p.gender,
          p.dateOfBirth ? `born ${dateLabel(p.dateOfBirth)}` : null,
          p.nationality,
          p.seatAssigned ? `seat ${p.seatAssigned}` : null,
          p.mealAssigned ? `meal ${p.mealAssigned}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        passportMasked: p.passportNumberMasked,
        passportExpiry: p.passportExpiry,
        panMasked: p.panNumberMasked,
        ticket: p.ticketNumber,
      }));

  const empty = (
    <div className="py-6 text-center text-sm text-zinc-500">
      No traveller details were saved on this booking.
    </div>
  );

  return (
    <UzCard
      title={isHotel ? 'Guests' : 'Travellers'}
      description={`${travellers.length} on this booking. Document numbers are masked; the eye reveals one at a time and is logged.`}
    >
      {/* Tablet / desktop: the table. */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-[11px] tracking-wide text-zinc-500 uppercase">
              <th className="py-2 pr-3 font-medium">Name</th>
              <th className="py-2 pr-3 font-medium">Details</th>
              <th className="py-2 pr-3 font-medium">Passport</th>
              <th className="py-2 pr-3 font-medium">PAN</th>
              {!isHotel && <th className="py-2 pr-3 font-medium">Ticket</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {travellers.map((t) => (
              <TravellerRow key={t.id} bookingId={booking.id} traveller={t} showTicket={!isHotel} />
            ))}
            {travellers.length === 0 && (
              <tr>
                <td colSpan={5} className="p-0">
                  {empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Phones: a card per traveller. */}
      <div className="flex flex-col divide-y divide-zinc-100 sm:hidden">
        {travellers.map((t) => (
          <TravellerMobileCard
            key={t.id}
            bookingId={booking.id}
            traveller={t}
            showTicket={!isHotel}
          />
        ))}
        {travellers.length === 0 && empty}
      </div>
    </UzCard>
  );
};

/**
 * One traveller's reveal state. Shared by the table row and the phone card
 * so both layouts make the same (logged) server call and toggle the same way.
 */
function useTravellerReveal(
  bookingId: string,
  travellerId: string,
): {
  shown: { passport: string | null; pan: string | null } | null;
  pending: boolean;
  toggle: () => void;
} {
  const reveal = useRevealDocuments();
  const [shown, setShown] = useState<{ passport: string | null; pan: string | null } | null>(null);

  const toggle = (): void => {
    if (shown) {
      setShown(null);
      return;
    }
    reveal.mutate(
      { bookingId, travellerId },
      { onSuccess: (r) => setShown({ passport: r.passportNumber, pan: r.panNumber }) },
    );
  };

  return { shown, pending: reveal.isPending, toggle };
}

/** "Lead traveller" / "Child" / ... - small, and set a little below the name. */
function RoleBadge({ role }: { role: string }): React.JSX.Element {
  return (
    <div className="mt-1">
      <UzBadge
        tone={role.startsWith('Lead') ? 'default' : 'secondary'}
        pill
        className="px-1.5 py-0 text-[10px] leading-4"
      >
        {role}
      </UzBadge>
    </div>
  );
}

function RevealButton({
  shown,
  pending,
  onToggle,
}: {
  shown: boolean;
  pending: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <UzTooltip label={shown ? 'Hide document numbers' : 'Reveal document numbers (logged)'}>
      <UzButton
        variant="ghost"
        size="sm"
        onClick={onToggle}
        loading={pending}
        aria-label={shown ? 'Hide document numbers' : 'Reveal document numbers'}
      >
        {shown ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </UzButton>
    </UzTooltip>
  );
}

function TravellerRow({
  bookingId,
  traveller,
  showTicket,
}: {
  bookingId: string;
  traveller: Traveller;
  showTicket: boolean;
}): React.JSX.Element {
  const { shown, pending, toggle } = useTravellerReveal(bookingId, traveller.id);
  const hasDocs = Boolean(traveller.passportMasked || traveller.panMasked);

  return (
    <tr>
      <td className="py-2.5 pr-3 align-top">
        <div className="font-medium text-zinc-900">{traveller.name}</div>
        <RoleBadge role={traveller.role} />
      </td>
      <td className="py-2.5 pr-3 align-top text-xs text-zinc-600">{traveller.extra || '-'}</td>
      <td className="py-2.5 pr-3 align-top font-mono text-xs">
        {traveller.passportMasked ? (
          <div className="flex flex-col gap-0.5">
            <span>{shown?.passport ?? traveller.passportMasked}</span>
            {traveller.passportExpiry && (
              <span className="font-sans text-[11px] text-zinc-500">
                exp {dateLabel(traveller.passportExpiry)}
              </span>
            )}
          </div>
        ) : (
          <span className="text-zinc-400">-</span>
        )}
      </td>
      <td className="py-2.5 pr-3 align-top font-mono text-xs">
        <div className="flex items-center gap-2">
          <span>
            {traveller.panMasked ? (
              (shown?.pan ?? traveller.panMasked)
            ) : (
              <span className="text-zinc-400">-</span>
            )}
          </span>
          {hasDocs && <RevealButton shown={shown !== null} pending={pending} onToggle={toggle} />}
        </div>
      </td>
      {showTicket && (
        <td className="py-2.5 pr-3 align-top font-mono text-xs">
          {traveller.ticket ?? <span className="text-zinc-400">-</span>}
        </td>
      )}
    </tr>
  );
}

function TravellerMobileCard({
  bookingId,
  traveller,
  showTicket,
}: {
  bookingId: string;
  traveller: Traveller;
  showTicket: boolean;
}): React.JSX.Element {
  const { shown, pending, toggle } = useTravellerReveal(bookingId, traveller.id);
  const hasDocs = Boolean(traveller.passportMasked || traveller.panMasked);
  const dash = <span className="text-zinc-400">-</span>;

  return (
    <div className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="text-sm font-medium wrap-break-word text-zinc-900">{traveller.name}</div>
          <RoleBadge role={traveller.role} />
        </div>
        {hasDocs && (
          <div className="shrink-0">
            <RevealButton shown={shown !== null} pending={pending} onToggle={toggle} />
          </div>
        )}
      </div>
      {traveller.extra && (
        <div className="text-xs wrap-break-word text-zinc-600">{traveller.extra}</div>
      )}
      <dl className="grid grid-cols-[minmax(64px,auto)_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-zinc-500">Passport</dt>
        <dd className="min-w-0 font-mono break-all">
          {traveller.passportMasked ? (
            <>
              {shown?.passport ?? traveller.passportMasked}
              {traveller.passportExpiry && (
                <span className="ml-2 font-sans text-[11px] text-zinc-500">
                  exp {dateLabel(traveller.passportExpiry)}
                </span>
              )}
            </>
          ) : (
            dash
          )}
        </dd>
        <dt className="text-zinc-500">PAN</dt>
        <dd className="min-w-0 font-mono break-all">
          {traveller.panMasked ? (shown?.pan ?? traveller.panMasked) : dash}
        </dd>
        {showTicket && (
          <>
            <dt className="text-zinc-500">Ticket</dt>
            <dd className="min-w-0 font-mono break-all">{traveller.ticket ?? dash}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
